// Import stylesheets for datepicker in index.tsx of app project
import type {
  InputProps
} from '@chakra-ui/react'
import {
  FormErrorMessage,
  forwardRef,
  Image,
  Input,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react'
import { isBefore, isValid } from 'date-fns'
import { Czech } from 'flatpickr/dist/l10n/cs'
import type { BaseOptions } from 'flatpickr/dist/types/options'
import type { ChangeEventHandler } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import ReactFlatpickr from 'react-flatpickr'
import type { FieldError } from 'react-hook-form'
import { useController, useFormContext } from 'react-hook-form'
const Flatpickr = ('default' in ReactFlatpickr ? ReactFlatpickr.default : ReactFlatpickr) as typeof ReactFlatpickr
type FlatpickrType = InstanceType<typeof Flatpickr>

import { customAddDays } from 'BrokerTsLib/src/extensions/date.extensions'
import calendarIcon from '../images/calendar-icon.svg'
import { formatDate } from '../Utils/formatDate'

interface SimpleDatepickerProps extends InputProps {
  date?: Date
  onDateChange: (date?: Date) => void
  isOpen?: boolean
  options?: Partial<BaseOptions>
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined
  errors?: FieldError | undefined
  onClose?: Function
  size?: 'sm' | 'md'
  showIcon?: boolean
}

type DatepickerProps = {
  isOpen?: boolean
  options?: Partial<BaseOptions>
  handleDateChange?: (date: Date | undefined) => void
} & Pick<InputProps, 'isDisabled' | 'placeholder' | 'autoComplete' | 'htmlSize'>

export const ReactHookFormDatepicker = ({ options, isOpen, handleDateChange, ...inputProps }: DatepickerProps & { name: string }) => {
  const { control } = useFormContext()
  const { name } = inputProps

  const {
    field: { ref, onChange, onBlur },
    fieldState: { error }
  } = useController({
    name, control
  })

  const { watch } = useFormContext()

  const onDateChange = (date: Date | undefined) => {
    onChange(date ?? '')
  }

  return (
    <BaseDatepicker
      ref={ref}
      date={watch(name)}
      onDateChange={handleDateChange ?? onDateChange}
      onBlur={onBlur}
      errors={error}
      options={options}
      isOpen={isOpen}
      {...inputProps}
    />
  )
}

// TODO: stejny uz s carkulkou. Spojit do brokerTsLib
export const BaseDatepicker = forwardRef((props: SimpleDatepickerProps, _ref) => {
  const { options, date: originalDate, isOpen = false, onClose, errors, isDisabled, size, showIcon = true } = props
  const { minDate, disable, } = options || {}
  const fp = useRef<FlatpickrType>(null)

  // useMemo kvuli zmene data v ramci formulare, napr. nastaveni data 1 registrace z VinOnline
  const oDate = useMemo(() => !originalDate || originalDate as any == '' ? undefined : new Date(originalDate), [originalDate])
  const [date, setDate] = useState(oDate)

  useEffect(() => {
    setDate(oDate)
  }, [oDate])

  const handleDateChange = (date?: Date) => {
    const d = date && isValid(new Date(date)) ? new Date(date) : undefined

    props.onDateChange(d)
    setDate(d)
  }

  useEffect(() => {
    if (isOpen) {
      fp?.current?.flatpickr?.open()
      fp?.current?.flatpickr?.input?.focus()
    }
    else
      fp?.current?.flatpickr?.close()
    return () => { }
  }, [isOpen])

  const errorMessage = errors && errors?.message

  useEffect(() => {
    if (minDate) {
      if (isBefore(date ?? new Date(), minDate as Date)) {
        const from = customAddDays(date ?? new Date(), 1)
        const fromSelectedDateToMinDay = {
          from: formatDate(from),
          to: formatDate(customAddDays(minDate as Date, -1)),
        }

        const disableFromSelectedToMin = [fromSelectedDateToMinDay]

        // If selected date is before minDate, set minDate to selected date
        fp.current?.flatpickr.set('minDate', date ?? from)
        // Disable all dates after selected date and before minDate
        fp.current?.flatpickr.set('disable', [...(disable ?? []), ...disableFromSelectedToMin])
        // Redraw flatpickr
        fp.current?.flatpickr.redraw()
      } else {
        fp.current?.flatpickr.set('minDate', minDate)
        fp.current?.flatpickr.set('disable', [{
          to: formatDate(customAddDays(minDate as Date, -1)),
        }
        ])
      }
    }

    return () => { }
  }, [minDate, date, disable])

  return (
    <>
      <Flatpickr
        ref={fp}
        options={{
          ...options,
          minDate: undefined,
          // allowInvalidPreload: true, // pokud je true, tak to pada na invalid date (napr kdyz se zada string)
          locale: Czech,
          animate: true,
          dateFormat: options?.enableTime ? 'd.m.Y H:i' : 'd.m.Y',
          allowInput: true,
          onClose: () => onClose?.()
        }}
        value={date}
        render={({ defaultValue, onBlur: inputOnBlur }, ref) => {
          return (
            <InputGroup size={size} h={size === 'sm' ? 8 : undefined}>
              {showIcon && <InputLeftElement
                w={size === 'sm' ? 8 : undefined}
                h={size === 'sm' ? 8 : undefined}
                pointerEvents="none"
                children={
                  <Image src={calendarIcon} width={size === 'sm' ? 3.5 : 4} height={size === 'sm' ? 3.5 : 4} alt="" />
                }
              />}

              <Input
                defaultValue={defaultValue}
                ref={ref}
                htmlSize={props.htmlSize ?? 9}
                isDisabled={isDisabled}
                pl={size === 'sm' ? 8 : undefined}
                onBlur={(e) => {
                  const text = e.currentTarget.value.trim()
                  if (text === '') {
                    if (date !== undefined) {
                      fp.current?.flatpickr.clear()
                      handleDateChange(undefined)
                    }
                  } else {
                    const parsed = fp.current?.flatpickr.parseDate(text, options?.enableTime ? 'd.m.Y H:i' : 'd.m.Y')
                    if (parsed && isValid(parsed)) {
                      // only commit if it differs from current date to avoid noisy onChange
                      if (!date || parsed.getTime() !== date.getTime()) {
                        fp.current?.flatpickr.setDate(parsed, true)
                      }
                    }
                    // invalid text → leave Flatpickr to revert on next render
                  }
                  inputOnBlur?.(e)
                }}
                variant={props.variant}
                sx={props.sx}
              />

            </InputGroup>
          )
        }}
        onChange={([date]) => handleDateChange(date)}
      />

      {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </>
  )
})